/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, computed, set } from '@ember/object';
import { or } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { dropTask } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import { isPotentiallyValidUrl } from 'cio/utils/validators/url';
import isValidEmail from 'cio/utils/validators/email';
import DS from 'ember-data';
import ApiErrorsService from 'cio/services/api-errors';
import DomainModel from 'cio/models/domain';
import IdentityModel from 'cio/models/identity';

interface AddDomainArgs {
  readOnly: boolean;
  domains: DS.RecordArray<DomainModel>;
  onCreate: () => unknown;
  onClose: () => unknown;
}

export const DomainValidations = {
  domain(this: AddDomain, value: string) {
    if (isEmpty(value)) {
      return 'Domain cannot be empty';
    }

    // Weak validation: just check if the URL is valid if http/https is added,
    // and that it includes at least one dot.
    if (!isPotentiallyValidUrl(value) || !value.includes('.')) {
      return 'Domain must be a valid URL';
    }

    // If the domain already exists
    if (this.args.domains.find((dM) => dM.domain === value && (dM.isNew as unknown as boolean) !== true) != null) {
      return 'A domain with that name already exists';
    }

    return true;
  },
};

const IdentityValidations = {
  email: {
    dependentKeys: ['domain'],
    validator(value: string) {
      // No email is fine: it means no sender identity will be added
      if (isEmpty(value)) {
        return true;
      }

      // If there is a value, we need to ensure it is valid when suffixed with the hostname
      if (!isValidEmail(value)) {
        return 'Email must be valid';
      }

      return true;
    },
  },
};

export default class AddDomain extends Component<AddDomainArgs> {
  @service store!: DS.Store;
  @service apiErrors!: ApiErrorsService;

  domain!: DomainModel;
  identity!: IdentityModel;

  domainValidations: FormValidations;
  identityValidations: FormValidations;

  @computed('domain.domain')
  get hostname() {
    let { domain } = this.domain;

    try {
      let parsed = new URL(domain);
      return parsed.hostname;
    } catch {
      // Not a valid URL, ignore errors. As long as it has dots, we're good.
      return domain.includes('.') ? domain : '';
    }
  }

  @or('domainValidations.hasErrors', 'identityValidations.hasErrors') hasErrors!: boolean;

  @action
  onChangeEmail(value: string) {
    set(this.identity, 'email', value);
    this.identityValidations.validateAttribute('email');
  }

  @action
  onDomainChange(domain: string) {
    set(this.domain, 'domain', domain);
    this.domainValidations.validateAttribute('domain');
  }

  @action
  onNameChange(name: string) {
    set(this.identity, 'name', name);
    this.identityValidations.validateAttribute('name');
  }

  saveTask = dropTask(async (ev: KeyboardEvent | MouseEvent) => {
    ev.preventDefault();

    await this.domainValidations.validateModel();
    await this.identityValidations.validateModel();

    try {
      await this.domain.save();
      if (!isEmpty(this.identity.email)) {
        await this.identity.save();
      }

      await this.args.onCreate();
    } catch (e) {
      // Validation errors are handled by FormValidations
      if (e.errors && e.errors[0]?.status !== '422' && e.errors[0]?.source == null) {
        this.apiErrors.report(e);
      }
    }
  });

  constructor(owner: unknown, args: AddDomainArgs) {
    super(owner, args);

    this.setupRecords();

    this.domainValidations = FormValidations.create({
      validations: DomainValidations,
      context: this,
      modelKey: 'domain',
    });

    this.identityValidations = FormValidations.create({
      validations: IdentityValidations,
      context: this,
      modelKey: 'identity',
    });
  }

  willDestroy() {
    super.willDestroy();

    if (this.domain?.get('isNew')) {
      this.domain?.unloadRecord();
    }

    if (this.identity?.get('isNew')) {
      this.identity?.unloadRecord();
    }
  }

  private setupRecords() {
    this.domain = this.store.createRecord('domain');
    this.identity = this.store.createRecord('identity', { templateType: 'email' });
  }
}
